/**
 * Estate map — direct port of the mockup's `initEstateMap` (vertailutyokalu.html
 * lines 9210–9700). Renders a Leaflet map with:
 *
 *   - **Maanmittauslaitos avoin WMTS** as the base map (Maastokartta default,
 *     Taustakartta + Ortokuva selectable via the layer control).
 *   - **MML kiinteistöjaotus + kiinteistötunnukset** as optional overlays.
 *   - The target estate's actual parcel polygon, fetched from the open
 *     Kiinteistö Features API by kiinteistötunnus and highlighted in red.
 *
 * Coordinate handling: MML services occasionally return geometries in axis-
 * order [lat, lon] or in EPSG:3067 (ETRS-TM35FIN, metres) even when EPSG:4326
 * is requested. The detector in `normalizeGeoJsonCRS` handles all three
 * cases and returns standard GeoJSON [lon, lat] in WGS84 degrees.
 *
 * **API key**: in DEV we use the same public-ish key the mockup ships with
 * (Suomen Metsätilastot Oy's MML key — restricted to allowed domains in
 * MML's omatili.maanmittauslaitos.fi panel). In production this should be
 * replaced with a backend proxy (`smt_mml_handler`, M6) so the key isn't
 * exposed to anyone hitting the JS bundle. The key can be overridden via
 * `VITE_MML_API_KEY` in `.env.local` for safer DEV testing.
 */
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { regionCenter } from '../data/regions.ts';
import { getLang, t } from '../i18n/index.ts';
import type { Estate } from '../types/api.ts';

const MML_API_KEY =
  (import.meta.env.VITE_MML_API_KEY as string | undefined) ||
  '09a47c0d-87f9-43b5-baa7-4cdb105eb180';

const WMTS_BASE = 'https://avoin-karttakuva.maanmittauslaitos.fi/avoin/wmts';
const TILEMATRIXSET = 'WGS84_Pseudo-Mercator';
const FEATURES_BASE =
  'https://avoin-paikkatieto.maanmittauslaitos.fi/kiinteisto-avoin/simple-features/v3/collections/PalstanSijaintitiedot/items';

interface InitOptions {
  /** DOM container for the map. Must already be sized via CSS. */
  el: HTMLElement;
}

let active: L.Map | null = null;

/**
 * Tear down a previous map (if any) and mount a fresh one for this estate.
 * Returns the Leaflet map handle so callers can chain UI bindings; usually
 * unnecessary because the layer control + popup are already wired.
 */
export async function mountEstateMap(estate: Estate, opts: InitOptions): Promise<L.Map> {
  // Tear down any active map first to avoid Leaflet "Map container is
  // already initialized" errors on view reload.
  if (active) {
    try {
      active.remove();
    } catch {
      /* ignore */
    }
    active = null;
  }

  // Initial centre = region centroid → reasonable starting view while the
  // parcel geometry is fetched. We override this with parcel bounds below.
  const fallback: [number, number] = regionCenter((estate.region as string) || '');

  const map = L.map(opts.el, {
    center: fallback,
    zoom: 9,
    minZoom: 5,
    maxZoom: 17,
    zoomControl: true,
    scrollWheelZoom: false, // matches mockup — avoid scroll-jacking
  });
  active = map;

  const maastokartta = makeWmtsLayer('maastokartta', 'image/png');
  const taustakartta = makeWmtsLayer('taustakartta', 'image/png');
  const ortokuva = makeWmtsLayer('ortokuva', 'image/jpeg');
  maastokartta.addTo(map);

  const kiinteistorajat = makeOverlayLayer('kiinteistojaotus', 'image/png', 0.5, 10);
  const kiinteistotunnukset = makeOverlayLayer('kiinteistotunnukset', 'image/png', 1.0, 13);

  L.control
    .layers(
      {
        [t('layer_terrain')]: maastokartta,
        [t('layer_map')]: taustakartta,
        [t('layer_aerial')]: ortokuva,
      },
      {
        [t('layer_parcels')]: kiinteistorajat,
        [t('layer_parcel_ids')]: kiinteistotunnukset,
      },
      { position: 'topright', collapsed: false },
    )
    .addTo(map);

  // North arrow control — bottom-left, matches mockup (`leaflet-north-arrow`).
  const NorthArrow = L.Control.extend({
    options: { position: 'bottomleft' as L.ControlPosition },
    onAdd: function () {
      const div = L.DomUtil.create('div', 'leaflet-north-arrow');
      div.innerHTML = '<div class="north-n">N</div><div class="north-needle">▲</div>';
      div.title = getLang() === 'en' ? 'North up' : 'Pohjoinen ylöspäin';
      return div;
    },
  });
  new NorthArrow().addTo(map);

  // Force a layout pass once the container becomes visible — Leaflet
  // measures the wrapper at construction time and a hidden parent yields
  // 0×0, which prevents tiles from loading until the next interaction.
  setTimeout(() => {
    try {
      map.invalidateSize();
    } catch {
      /* ignore */
    }
  }, 100);

  // Fetch + render the actual parcel polygon by kiinteistötunnus.
  if (estate.property_id) {
    const palsta = await fetchPalstaGeometry(estate.property_id).catch((e) => {
      console.warn('palsta fetch failed', e);
      return null;
    });
    if (palsta && palsta.features?.length) {
      const layer = L.geoJSON(palsta, {
        style: {
          color: 'rgb(164, 71, 72)',
          weight: 3,
          opacity: 0.95,
          fillColor: 'rgb(164, 71, 72)',
          fillOpacity: 0.15,
        },
      }).addTo(map);

      const popup =
        `<strong>${escapeHtml(estate.name || '')}</strong><br>` +
        `<code>${escapeHtml(estate.property_id)}</code><br>` +
        `${escapeHtml(estate.municipality || '')}`;
      layer.bindPopup(popup);

      try {
        const bounds = layer.getBounds();
        if (bounds.isValid()) {
          map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
        }
      } catch {
        /* ignore */
      }
      return map;
    }
  }

  // Fallback: drop a marker at the region centroid so the user sees an
  // approximate location even when the parcel lookup fails.
  L.marker(fallback)
    .addTo(map)
    .bindPopup(escapeHtml(estate.municipality || (estate.region as string) || ''));
  return map;
}

export function disposeEstateMap(): void {
  if (active) {
    try {
      active.remove();
    } catch {
      /* ignore */
    }
    active = null;
  }
}

// -----------------------------------------------------------------------------
// MML WMTS helpers
// -----------------------------------------------------------------------------

function makeWmtsLayer(layer: string, fmt: string): L.TileLayer {
  const url =
    `${WMTS_BASE}?api-key=${MML_API_KEY}` +
    `&service=WMTS&request=GetTile&version=1.0.0` +
    `&layer=${layer}&style=default&format=${fmt}` +
    `&tilematrixset=${TILEMATRIXSET}` +
    `&TileMatrix={z}&TileRow={y}&TileCol={x}`;
  return L.tileLayer(url, {
    attribution: '© Maanmittauslaitos',
    tileSize: 256,
    minZoom: 5,
    maxZoom: 17,
  });
}

function makeOverlayLayer(
  layer: string,
  fmt: string,
  opacity: number,
  minZoom: number,
): L.TileLayer {
  const url =
    `${WMTS_BASE}?api-key=${MML_API_KEY}` +
    `&service=WMTS&request=GetTile&version=1.0.0` +
    `&layer=${layer}&style=default&format=${fmt}` +
    `&tilematrixset=${TILEMATRIXSET}` +
    `&TileMatrix={z}&TileRow={y}&TileCol={x}`;
  return L.tileLayer(url, {
    attribution: '© Maanmittauslaitos',
    tileSize: 256,
    minZoom,
    maxZoom: 17,
    opacity,
  });
}

// -----------------------------------------------------------------------------
// Kiinteistö Features API
// -----------------------------------------------------------------------------

/**
 * Convert a human-readable kiinteistötunnus ("977-405-32-0", "977-405-32-0001")
 * into the 14-char zero-padded form the MML API expects ("97740500320000").
 * Format: K(3)-S(3)-R(4)-Y(4) zero-padded.
 */
export function normalizeKiinteistoId(id: string | undefined | null): string | null {
  if (!id) return null;
  const parts = String(id).split('-').map((p) => p.trim());
  if (parts.length !== 4) return null;
  const widths = [3, 3, 4, 4];
  try {
    return parts.map((p, i) => p.padStart(widths[i], '0')).join('');
  } catch {
    return null;
  }
}

interface GeoJsonGeometry {
  type: string;
  coordinates: unknown;
}
interface GeoJsonFeature {
  type: 'Feature';
  geometry: GeoJsonGeometry | null;
  properties?: Record<string, unknown>;
}
interface GeoJsonFC {
  type: 'FeatureCollection';
  features: GeoJsonFeature[];
}

/**
 * Fetch palsta (parcel) geometry for one or more comma-separated
 * kiinteistötunnus values. Returns a normalised GeoJSON FeatureCollection
 * in EPSG:4326 [lon, lat] axis order, or null if nothing resolved.
 */
export async function fetchPalstaGeometry(
  kiinteistoIdString: string,
): Promise<GeoJsonFC | null> {
  if (!kiinteistoIdString) return null;
  const ids = String(kiinteistoIdString)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (!ids.length) return null;

  const results = await Promise.all(
    ids.map(async (id) => {
      const normalized = normalizeKiinteistoId(id);
      if (!normalized) return null;
      const url =
        `${FEATURES_BASE}?kiinteistotunnus=${normalized}` +
        `&crs=http://www.opengis.net/def/crs/EPSG/0/4326&f=json` +
        `&api-key=${MML_API_KEY}`;
      try {
        const r = await fetch(url, {
          headers: { Accept: 'application/geo+json, application/json' },
        });
        if (!r.ok) return null;
        const data = (await r.json()) as GeoJsonFC;
        if (!data?.features?.length) return null;
        return normalizeGeoJsonCRS(data);
      } catch {
        return null;
      }
    }),
  );

  const all: GeoJsonFeature[] = [];
  for (const r of results) {
    if (r?.features) all.push(...r.features);
  }
  return all.length ? { type: 'FeatureCollection', features: all } : null;
}

// -----------------------------------------------------------------------------
// Coordinate normalisation
// -----------------------------------------------------------------------------

/**
 * Detects the input axis order and reprojects (when needed) to standard
 * GeoJSON: EPSG:4326 with [lon, lat] order in degrees. Modes:
 *   - lonlat   → already correct
 *   - latlon   → swap pairs
 *   - tm35fin  → reproject from EPSG:3067 (metres) to EPSG:4326
 */
export function normalizeGeoJsonCRS(gj: GeoJsonFC): GeoJsonFC {
  if (!gj.features.length) return gj;
  const sample = getFirstCoord(gj.features[0].geometry);
  if (!sample) return gj;
  const [a, b] = sample;

  let mode: 'lonlat' | 'latlon' | 'tm35fin' | 'unknown' = 'unknown';
  if (a > -180 && a < 180 && b > -90 && b < 90 && a > 19 && a < 33 && b > 59 && b < 71) {
    mode = 'lonlat';
  } else if (a > 59 && a < 71 && b > 19 && b < 33) {
    mode = 'latlon';
  } else if (a > 50000 && a < 800000 && b > 6500000 && b < 7800000) {
    mode = 'tm35fin';
  }
  if (mode === 'lonlat' || mode === 'unknown') return gj;

  const transform =
    mode === 'latlon'
      ? (([x, y]: number[]) => [y, x])
      : (([e, n]: number[]) => tm35finToWgs84(e, n));

  function walk(coords: unknown): unknown {
    if (Array.isArray(coords) && typeof coords[0] === 'number') {
      const c = coords as number[];
      const [x, y] = transform([c[0], c[1]]);
      return c.length > 2 ? [x, y, c[2]] : [x, y];
    }
    if (Array.isArray(coords)) return coords.map(walk);
    return coords;
  }

  const out: GeoJsonFC = JSON.parse(JSON.stringify(gj));
  out.features.forEach((f) => {
    if (f.geometry?.coordinates) {
      f.geometry.coordinates = walk(f.geometry.coordinates);
    }
  });
  return out;
}

function getFirstCoord(geom: GeoJsonGeometry | null): [number, number] | null {
  if (!geom?.coordinates) return null;
  let c: unknown = geom.coordinates;
  while (Array.isArray(c) && Array.isArray(c[0])) c = c[0];
  if (Array.isArray(c) && typeof c[0] === 'number') {
    return [c[0] as number, c[1] as number];
  }
  return null;
}

/**
 * Convert ETRS-TM35FIN (EPSG:3067) → WGS84 (EPSG:4326).
 * Direct port from mockup vertailutyokalu.html:9171. Uses the inverse
 * transverse-Mercator series with central meridian 27°E, GRS80 ellipsoid,
 * scale 0.9996, false easting 500000, false northing 0.
 */
export function tm35finToWgs84(E: number, N: number): [number, number] {
  const a = 6378137.0;
  const f = 1 / 298.257222101;
  const b = a * (1 - f);
  const e2 = (a * a - b * b) / (a * a);
  const ep2 = (a * a - b * b) / (b * b);
  const k0 = 0.9996;
  const lon0 = (27 * Math.PI) / 180;
  const FE = 500000;
  const FN = 0;
  const x = N - FN;
  const y = E - FE;
  const M = x / k0;
  const e1 = (1 - Math.sqrt(1 - e2)) / (1 + Math.sqrt(1 - e2));
  const mu = M / (a * (1 - e2 / 4 - (3 * e2 * e2) / 64 - (5 * e2 * e2 * e2) / 256));
  const phi1 =
    mu +
    ((3 * e1) / 2 - (27 * e1 * e1 * e1) / 32) * Math.sin(2 * mu) +
    ((21 * e1 * e1) / 16 - (55 * e1 * e1 * e1 * e1) / 32) * Math.sin(4 * mu) +
    ((151 * e1 * e1 * e1) / 96) * Math.sin(6 * mu) +
    ((1097 * e1 * e1 * e1 * e1) / 512) * Math.sin(8 * mu);
  const sinPhi1 = Math.sin(phi1);
  const cosPhi1 = Math.cos(phi1);
  const tanPhi1 = sinPhi1 / cosPhi1;
  const N1 = a / Math.sqrt(1 - e2 * sinPhi1 * sinPhi1);
  const T1 = tanPhi1 * tanPhi1;
  const C1 = ep2 * cosPhi1 * cosPhi1;
  const R1 = (a * (1 - e2)) / Math.pow(1 - e2 * sinPhi1 * sinPhi1, 1.5);
  const D = y / (N1 * k0);
  const lat =
    phi1 -
    ((N1 * tanPhi1) / R1) *
      ((D * D) / 2 -
        ((5 + 3 * T1 + 10 * C1 - 4 * C1 * C1 - 9 * ep2) * Math.pow(D, 4)) / 24 +
        ((61 + 90 * T1 + 298 * C1 + 45 * T1 * T1 - 252 * ep2 - 3 * C1 * C1) *
          Math.pow(D, 6)) /
          720);
  const lon =
    lon0 +
    (D -
      ((1 + 2 * T1 + C1) * Math.pow(D, 3)) / 6 +
      ((5 - 2 * C1 + 28 * T1 - 3 * C1 * C1 + 8 * ep2 + 24 * T1 * T1) * Math.pow(D, 5)) / 120) /
      cosPhi1;
  return [(lon * 180) / Math.PI, (lat * 180) / Math.PI];
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
